import { Suspense, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import useCurrentUser from 'store/currentUser';
import useSelectedLanguage from 'store/language';
import useSport from 'store/sport';
import useWebsiteSports, { SportProviders } from 'store/websiteSports';
import SectionLoader from 'components/sections/SectionLoader/SectionLoader';

import Atlas from './Atlas';
import BetBy from './BetBy';
import LatroBet from './LatroBet';
import OneXBet from './OneXBet';
import SoftSwiss from './SoftSwiss';
import TurboStars from './TurboStars';
import NineWickets from './nineWickets';
import DigitalSportsTech from './DigitalSportsTech';
import { hasPrematchType } from 'utils/predicates';
import SportFooterForSingleTypes from './SportFooterForSingleTypes';

const Sport = () => {
  const location = useLocation();

  const [userState] = useCurrentUser();
  const [state] = useWebsiteSports();
  const [language] = useSelectedLanguage();
  const [, sportActions] = useSport();

  const isLoading = state.status === 'loading';

  const sportId = useMemo(() => {
    const fragments = location.pathname.split('/');
    const fragment = fragments?.[fragments.length - 1];
    const sportId = fragment || state?.data?.[0]?.id;

    return sportId && Number(sportId);
  }, [location.pathname, state?.data]);

  const userExists = !!userState.data.id;

  useEffect(() => {
    if (sportId) {
      sportActions.read(sportId);
    }
  }, [userExists, sportId, language.id]);

  useEffect(() => {
    if (sportId !== SportProviders.BET_BY_ID) {
      document.body.classList.add('overflow-hidden');

      return () => {
        document.body.classList.remove('overflow-hidden');
      };
    }
  }, [sportId]);

  const SportComponent = useMemo(() => {
    if (sportId === SportProviders.ATLAS_ID) return Atlas;

    if (sportId === SportProviders.BET_BY_ID) return BetBy;

    if (sportId === SportProviders.SOFT_SWISS_ID) return SoftSwiss;

    if (sportId === SportProviders.DIGITAL_SPORT_TECH) return DigitalSportsTech;

    if (sportId === SportProviders.NINE_WICKETS) return NineWickets;

    if (sportId === SportProviders.ONE_X_BET) return OneXBet;

    if (sportId === SportProviders.TURBO_STARS) return TurboStars;

    if (sportId === SportProviders.LATRO_BET) return LatroBet;

    console.error('Sport not found', sportId);

    return null;
  }, [sportId]);

  if (!SportComponent || isLoading) {
    return <SectionLoader />;
  }

  const websiteSport = state.data.find((sport) => sport.id === sportId);

  const isSingleTypeSportFooterVisible = websiteSport?.types.length === 1;
  const hasPrematch = hasPrematchType(websiteSport);

  return (
    <Suspense fallback={<SectionLoader />}>
      {SportComponent && <SportComponent sportId={String(sportId)} />}
      {isSingleTypeSportFooterVisible && <SportFooterForSingleTypes hasPremtach={hasPrematch} />}
    </Suspense>
  );
};

export default Sport;
